<script setup>
import { reactive, computed, watch, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import whatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { DirectUpload } from 'activestorage';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

// Providers that use templates (official API)
const TEMPLATE_PROVIDERS = ['whatsapp_cloud', 'notificame'];
// Providers that use direct messages (unofficial API)
const MESSAGE_PROVIDERS = ['evolution', 'waha', 'uazapi'];

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
  funnels: useMapGetter('funnels/getFunnels'),
  accountId: useMapGetter('getCurrentAccountId'),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  message: '',
  scheduledAt: null,
  audienceType: 'tags',
  selectedAudience: [],
  selectedFunnel: null,
  selectedStage: null,
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);
const isSyncingTemplates = ref(false);
const attachedFiles = ref([]);
const isUploading = ref(false);
const fileInputRef = ref(null);
const csvFileInputRef = ref(null);
const csvContacts = ref([]);
const csvParseError = ref('');
const isDraggingCsv = ref(false);
const csvColumns = ref([]);
const columnMapping = ref({});

// Get the selected inbox object
const selectedInbox = computed(() => {
  if (!state.inboxId) return null;
  return formState.inboxes.value.find(inbox => inbox.id === state.inboxId);
});

// Get provider of selected inbox
const selectedProvider = computed(() => {
  return selectedInbox.value?.provider || null;
});

// Check if selected inbox uses template-based messaging (official API)
const isTemplateProvider = computed(() => {
  return TEMPLATE_PROVIDERS.includes(selectedProvider.value);
});

// Check if selected inbox uses message-based messaging (unofficial API)
const isMessageProvider = computed(() => {
  return MESSAGE_PROVIDERS.includes(selectedProvider.value);
});

// Provider display name for UI
const providerDisplayName = computed(() => {
  const names = {
    whatsapp_cloud: 'WhatsApp Cloud API',
    notificame: 'Notificame',
    evolution: 'Evolution API',
    waha: 'WAHA',
    uazapi: 'Uazapi',
  };
  return names[selectedProvider.value] || selectedProvider.value;
});

// Dynamic validation rules based on audience type and provider
const rules = computed(() => {
  const baseRules = {
    title: { required, minLength: minLength(1) },
    inboxId: { required },
    scheduledAt: { required },
  };

  // Template is required only for official API providers
  if (isTemplateProvider.value) {
    baseRules.templateId = { required };
  }

  // Message is required only for unofficial API providers (unless files attached)
  if (isMessageProvider.value && attachedFiles.value.length === 0) {
    baseRules.message = { required, minLength: minLength(1) };
  }

  // Add audience-specific validation
  if (state.audienceType === 'tags') {
    baseRules.selectedAudience = { required };
  } else if (state.audienceType === 'funnel') {
    baseRules.selectedFunnel = { required };
    baseRules.selectedStage = { required };
  }
  // CSV validation is handled separately (csvContacts must have entries)

  return baseRules;
});

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceTypeOptions = computed(() => [
  {
    value: 'tags',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_TYPE.TAGS'),
  },
  {
    value: 'csv',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_TYPE.CSV'),
  },
  {
    value: 'funnel',
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_TYPE.FUNNEL'),
  },
]);

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() => {
  return formState.inboxes.value.map(inbox => {
    const providerLabel = {
      whatsapp_cloud: '(Cloud API)',
      notificame: '(Notificame)',
      evolution: '(Evolution)',
      waha: '(WAHA)',
      uazapi: '(Uazapi)',
    };
    const suffix = providerLabel[inbox.provider] || '';
    return {
      value: inbox.id,
      label: `${inbox.name} ${suffix}`.trim(),
    };
  });
});

const funnelOptions = computed(() =>
  mapToOptions(formState.funnels.value, 'id', 'name')
);

const stageOptions = computed(() => {
  if (!state.selectedFunnel) return [];
  const funnel = formState.funnels.value.find(
    f => f.id === state.selectedFunnel
  );
  if (!funnel?.stages) return [];

  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([stageName, stageConfig]) => ({
      value: stageName,
      label: stageConfig.label || stageName,
    }));
});

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field]?.$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => {
  const errors = {
    title: getErrorMessage('title', 'TITLE'),
    inbox: getErrorMessage('inboxId', 'INBOX'),
    scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  };

  if (isTemplateProvider.value) {
    errors.template = getErrorMessage('templateId', 'TEMPLATE');
  }

  if (isMessageProvider.value) {
    errors.message = getErrorMessage('message', 'MESSAGE');
  }

  if (state.audienceType === 'tags') {
    errors.audience = getErrorMessage('selectedAudience', 'AUDIENCE');
  } else if (state.audienceType === 'funnel') {
    errors.funnel = getErrorMessage('selectedFunnel', 'FUNNEL');
    errors.stage = getErrorMessage('selectedStage', 'STAGE');
  }

  return errors;
});

const hasRequiredTemplateParams = computed(() => {
  if (!isTemplateProvider.value) return true;
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isCsvValid = computed(() => {
  if (state.audienceType !== 'csv') return true;
  return csvContacts.value.length > 0;
});

// Extract body variables ({{1}}, {{2}}, etc.) from the selected template
const templateBodyVariables = computed(() => {
  if (!selectedTemplate.value) return [];
  const bodyComp = selectedTemplate.value.components?.find(
    c => c.type === 'BODY'
  );
  if (!bodyComp?.text) return [];
  const matches = bodyComp.text.match(/\{\{(\d+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
});

// Show column mapping UI when: CSV audience + template provider + extra columns + template variables
const showColumnMapping = computed(() => {
  return (
    state.audienceType === 'csv' &&
    isTemplateProvider.value &&
    csvColumns.value.length > 0 &&
    templateBodyVariables.value.length > 0 &&
    csvContacts.value.length > 0
  );
});

// Options for the column mapping ComboBox (includes "name" as a mappable column)
const csvColumnOptions = computed(() => {
  const options = [{ value: 'name', label: 'name' }];
  csvColumns.value.forEach(col => {
    options.push({ value: col, label: col });
  });
  return options;
});

// Check if all template variables have been mapped to CSV columns
const isMappingComplete = computed(() => {
  if (!showColumnMapping.value) return true;
  return templateBodyVariables.value.every(
    varKey => columnMapping.value[varKey]
  );
});

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasRequiredTemplateParams.value ||
    isUploading.value ||
    !isCsvValid.value ||
    !isMappingComplete.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, { ...initialState });
  attachedFiles.value = [];
  csvContacts.value = [];
  csvColumns.value = [];
  columnMapping.value = {};
  csvParseError.value = '';
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const syncTemplates = async () => {
  if (!state.inboxId) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.SYNC_TEMPLATES.SELECT_INBOX'));
    return;
  }

  try {
    isSyncingTemplates.value = true;

    await whatsappTemplatesAPI.syncTemplates(
      formState.accountId.value,
      state.inboxId
    );

    await store.dispatch('inboxes/get');

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.SYNC_TEMPLATES.SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.SYNC_TEMPLATES.ERROR'));
  } finally {
    isSyncingTemplates.value = false;
  }
};

// File upload helper function
const uploadSingleFile = file => {
  return new Promise((resolve, reject) => {
    const directUploadUrl = '/rails/active_storage/direct_uploads';
    const upload = new DirectUpload(file, directUploadUrl);

    upload.create((error, blob) => {
      if (error) {
        reject(error);
      } else {
        resolve(blob);
      }
    });
  });
};

// File upload handling for unofficial API providers
const handleFileSelect = async event => {
  const files = Array.from(event.target.files);
  const maxSize = 40 * 1024 * 1024; // 40MB limit

  isUploading.value = true;

  const uploadPromises = files
    .filter(file => {
      if (!checkFileSizeLimit(file, maxSize)) {
        useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.SIZE_ERROR'));
        return false;
      }
      return true;
    })
    .map(async file => {
      try {
        const blob = await uploadSingleFile(file);
        return {
          id: blob.id,
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
          blob_id: blob.id,
        };
      } catch (error) {
        useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.UPLOAD_ERROR'));
        return null;
      }
    });

  const results = await Promise.all(uploadPromises);
  const validFiles = results.filter(Boolean);
  attachedFiles.value.push(...validFiles);

  isUploading.value = false;

  // Clear the input
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const removeFile = index => {
  attachedFiles.value.splice(index, 1);
};

// Phone and name column aliases for CSV header matching
const PHONE_ALIASES = [
  'phone',
  'telefone',
  'numero',
  'number',
  'celular',
  'whatsapp',
  'tel',
];
const NAME_ALIASES = [
  'name',
  'nome',
  'first_name',
  'paciente',
  'cliente',
  'contato',
];

// CSV parsing and handling - extracts ALL columns for template variable mapping
const parseCSV = text => {
  const lines = text.split(/\r\n|\n/).filter(line => line.trim());
  if (lines.length === 0) return { contacts: [], extraColumns: [] };

  // Detect delimiter (comma or semicolon)
  const firstLine = lines[0];
  const delimiter = firstLine.includes(';') ? ';' : ',';

  // Parse header
  const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase());
  const phoneIndex = headers.findIndex(h => PHONE_ALIASES.includes(h));
  const nameIndex = headers.findIndex(h => NAME_ALIASES.includes(h));

  if (phoneIndex === -1) {
    return { error: 'phone_column_not_found' };
  }

  // Identify extra columns (everything that is not phone or name)
  const extraColumns = headers
    .map((h, i) => ({ name: h, index: i }))
    .filter(col => col.index !== phoneIndex && col.index !== nameIndex);

  const contacts = lines.slice(1).reduce((acc, line) => {
    const values = line.split(delimiter).map(v => v.trim());
    const phone = values[phoneIndex];

    if (phone) {
      // Clean phone number - keep only digits
      const cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone.length >= 10) {
        // Build extra data object from all non-phone/name columns
        const data = {};
        extraColumns.forEach(col => {
          const val = values[col.index];
          if (val !== undefined && val !== '') {
            data[col.name] = val;
          }
        });

        acc.push({
          phone: cleanPhone,
          name: nameIndex !== -1 ? values[nameIndex] || '' : '',
          data: Object.keys(data).length > 0 ? data : undefined,
        });
      }
    }
    return acc;
  }, []);

  return {
    contacts,
    extraColumns: extraColumns.map(c => c.name),
  };
};

const handleCSVFileSelect = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  csvParseError.value = '';

  if (!file.name.endsWith('.csv')) {
    csvParseError.value = t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.INVALID_FILE');
    return;
  }

  try {
    const text = await file.text();
    const result = parseCSV(text);

    if (result.error) {
      csvParseError.value = t(
        'CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.COLUMN_NOT_FOUND'
      );
      return;
    }

    csvContacts.value = result.contacts;
    csvColumns.value = result.extraColumns || [];
    // Reset column mapping when new CSV is loaded
    columnMapping.value = {};

    if (result.contacts.length === 0) {
      csvParseError.value = t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.NO_CONTACTS');
    }
  } catch {
    csvParseError.value = t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.PARSE_ERROR');
  }

  // Clear input
  if (csvFileInputRef.value) {
    csvFileInputRef.value.value = '';
  }
};

const handleCsvDrop = async event => {
  event.preventDefault();
  isDraggingCsv.value = false;

  const file = event.dataTransfer?.files?.[0];
  if (!file) return;

  // Create a synthetic event with the file
  const syntheticEvent = { target: { files: [file] } };
  await handleCSVFileSelect(syntheticEvent);
};

const handleCsvDragOver = event => {
  event.preventDefault();
  isDraggingCsv.value = true;
};

const handleCsvDragLeave = () => {
  isDraggingCsv.value = false;
};

const clearCsvContacts = () => {
  csvContacts.value = [];
  csvColumns.value = [];
  columnMapping.value = {};
  csvParseError.value = '';
};

const downloadCsvTemplate = () => {
  // Include extra variable columns when a template with variables is selected
  const extraCols = templateBodyVariables.value.map(v => `variable_${v}`);
  const headerRow = ['phone', 'name', ...extraCols].join(',');
  const exampleExtra = extraCols.map(() => '').join(',');
  const csvContent =
    extraCols.length > 0
      ? `${headerRow}\n5511999999999,João Silva${exampleExtra ? ',' + exampleExtra : ''}\n5511888888888,Maria Santos${exampleExtra ? ',' + exampleExtra : ''}`
      : 'phone,name\n5511999999999,João Silva\n5511888888888,Maria Santos';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'campaign_contacts_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const formatVariableLabel = key => `\u007B\u007B${key}\u007D\u007D`;

const formatFileSize = bytes => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const prepareCampaignDetails = () => {
  let audience = [];
  if (state.audienceType === 'tags') {
    audience = state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    }));
  } else if (state.audienceType === 'csv') {
    audience = csvContacts.value.map(contact => ({
      phone: contact.phone,
      name: contact.name,
      type: 'CSV',
      ...(contact.data ? { data: contact.data } : {}),
    }));
  } else if (state.audienceType === 'funnel') {
    audience = [
      {
        funnel_id: state.selectedFunnel,
        stage_name: state.selectedStage,
        type: 'FunnelStage',
      },
    ];
  }

  // For template-based providers (official API)
  if (isTemplateProvider.value) {
    const currentTemplate = selectedTemplate.value;
    const parserData = templateParserRef.value;
    const templateContent = parserData?.renderedTemplate || '';

    const templateParams = {
      name: currentTemplate?.name || '',
      namespace: currentTemplate?.namespace || '',
      category: currentTemplate?.category || 'UTILITY',
      language: currentTemplate?.language || 'en_US',
      processed_params: parserData?.processedParams || {},
    };

    // Include column mapping when CSV columns are mapped to template variables
    if (
      Object.keys(columnMapping.value).length > 0 &&
      state.audienceType === 'csv'
    ) {
      templateParams.column_mapping = { ...columnMapping.value };
    }

    return {
      title: state.title,
      message: templateContent,
      template_params: templateParams,
      inbox_id: state.inboxId,
      scheduled_at: formatToUTCString(state.scheduledAt),
      audience_type: state.audienceType,
      audience: audience,
    };
  }

  // For message-based providers (unofficial API)
  return {
    title: state.title,
    message: state.message,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience_type: state.audienceType,
    audience: audience,
    attachments: attachedFiles.value.map(f => f.blob_id),
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Reset template/message when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.message = '';
    attachedFiles.value = [];
  }
);

// Reset audience selections when audience type changes
watch(
  () => state.audienceType,
  () => {
    state.selectedAudience = [];
    state.selectedFunnel = null;
    state.selectedStage = null;
    csvContacts.value = [];
    csvColumns.value = [];
    columnMapping.value = {};
    csvParseError.value = '';
  }
);

// Reset stage when funnel changes
watch(
  () => state.selectedFunnel,
  () => {
    state.selectedStage = null;
  }
);

// Load funnels on component mount
onMounted(async () => {
  try {
    if (!formState.funnels.value || formState.funnels.value.length === 0) {
      await store.dispatch('funnels/get');
    }
  } catch (error) {
    // Error loading funnels - silently fail
  }
});
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <!-- Provider indicator badge -->
      <div v-if="selectedProvider" class="mt-1">
        <span
          class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
          :class="[
            isTemplateProvider
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
          ]"
        >
          {{ providerDisplayName }}
          <span class="ml-1 text-xs opacity-75">
            {{
              isTemplateProvider
                ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.PROVIDER.OFFICIAL')
                : t('CAMPAIGN.WHATSAPP.CREATE.FORM.PROVIDER.UNOFFICIAL')
            }}
          </span>
        </span>
      </div>
    </div>

    <!-- Template Selection (for official API providers) -->
    <div v-if="isTemplateProvider" class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <div class="flex gap-2">
        <ComboBox
          id="template"
          v-model="state.templateId"
          :options="templateOptions"
          :has-error="!!formErrors.template"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
          :message="formErrors.template"
          class="flex-1 [&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <Button
          variant="outlined"
          color="blue"
          type="button"
          :is-loading="isSyncingTemplates"
          :disabled="!state.inboxId || isSyncingTemplates"
          @click="syncTemplates"
        >
          <svg
            class="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SYNC_TEMPLATES.BUTTON') }}
        </Button>
      </div>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser (for official API providers) -->
    <WhatsAppTemplateParser
      v-if="isTemplateProvider && selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
      :hide-body-variables="showColumnMapping"
    />

    <!-- Message Input (for unofficial API providers) -->
    <div v-if="isMessageProvider" class="flex flex-col gap-1">
      <label for="message" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.LABEL') }}
      </label>
      <textarea
        id="message"
        v-model="state.message"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.PLACEHOLDER')"
        rows="4"
        class="w-full px-3 py-2 text-sm border rounded-lg resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand dark:border-n-weak"
        :class="{ 'border-n-ruby-9': formErrors.message }"
      />
      <p v-if="formErrors.message" class="mt-1 text-xs text-n-ruby-9">
        {{ formErrors.message }}
      </p>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.INFO') }}
      </p>
    </div>

    <!-- File Attachments (for unofficial API providers) -->
    <div v-if="isMessageProvider" class="flex flex-col gap-2">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.LABEL') }}
      </label>

      <!-- Upload button -->
      <div class="flex items-center gap-2">
        <input
          ref="fileInputRef"
          type="file"
          multiple
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          class="hidden"
          @change="handleFileSelect"
        />
        <Button
          variant="outlined"
          color="slate"
          type="button"
          :is-loading="isUploading"
          :disabled="isUploading"
          @click="fileInputRef?.click()"
        >
          <svg
            class="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
          </svg>
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.BUTTON') }}
        </Button>
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.HINT') }}
        </span>
      </div>

      <!-- Attached files list -->
      <div v-if="attachedFiles.length > 0" class="flex flex-col gap-2 mt-2">
        <div
          v-for="(file, index) in attachedFiles"
          :key="file.id"
          class="flex items-center justify-between p-2 rounded-lg bg-n-alpha-black2"
        >
          <div class="flex items-center gap-2">
            <svg
              class="w-4 h-4 text-n-slate-11"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span class="text-sm text-n-slate-12">{{ file.filename }}</span>
            <span class="text-xs text-n-slate-11">
              ({{ formatFileSize(file.byte_size) }})
            </span>
          </div>
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-black3"
            @click="removeFile(index)"
          >
            <svg
              class="w-4 h-4 text-n-slate-11"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Audience Type Selector -->
    <div class="flex flex-col gap-1">
      <label
        for="audienceType"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_TYPE.LABEL') }}
      </label>
      <ComboBox
        id="audienceType"
        v-model="state.audienceType"
        :options="audienceTypeOptions"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_TYPE.PLACEHOLDER')
        "
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Tags Audience -->
    <div v-if="state.audienceType === 'tags'" class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- CSV Import -->
    <div v-if="state.audienceType === 'csv'" class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.TITLE') }}
        </label>
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.DESCRIPTION') }}
        </p>
      </div>

      <!-- Download template button -->
      <div class="flex items-center gap-2">
        <Button
          variant="outlined"
          color="slate"
          type="button"
          size="sm"
          @click="downloadCsvTemplate"
        >
          <svg
            class="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.DOWNLOAD_TEMPLATE') }}
        </Button>
      </div>

      <!-- Drag and drop zone -->
      <div
        v-if="csvContacts.length === 0"
        class="relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer"
        :class="[
          isDraggingCsv
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-slate-6 hover:border-n-slate-8',
        ]"
        @click="csvFileInputRef?.click()"
        @drop="handleCsvDrop"
        @dragover="handleCsvDragOver"
        @dragleave="handleCsvDragLeave"
      >
        <input
          ref="csvFileInputRef"
          type="file"
          accept=".csv"
          class="hidden"
          @change="handleCSVFileSelect"
        />
        <svg
          class="mx-auto h-10 w-10 text-n-slate-9"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.UPLOAD_LABEL') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-9">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.DROP_HINT') }}
        </p>
      </div>

      <!-- Error message -->
      <p v-if="csvParseError" class="text-sm text-n-ruby-9">
        {{ csvParseError }}
      </p>

      <!-- Loaded contacts summary -->
      <div
        v-if="csvContacts.length > 0"
        class="p-4 rounded-lg bg-n-alpha-black2 border border-n-slate-6"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 dark:bg-green-900"
            >
              <svg
                class="w-5 h-5 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.CONTACTS_LOADED') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.CONTACTS_COUNT', {
                    count: csvContacts.length,
                  })
                }}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            color="slate"
            type="button"
            size="sm"
            @click="clearCsvContacts"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
        </div>

        <!-- Preview first 5 contacts -->
        <div
          v-if="csvContacts.length > 0"
          class="mt-3 pt-3 border-t border-n-slate-6"
        >
          <p class="text-xs text-n-slate-11 mb-2">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.PREVIEW') }}
          </p>
          <div class="space-y-1">
            <div
              v-for="(contact, index) in csvContacts.slice(0, 5)"
              :key="index"
              class="flex items-center gap-2 text-xs text-n-slate-12"
            >
              <span class="font-mono">{{ contact.phone }}</span>
              <span v-if="contact.name" class="text-n-slate-11">
                - {{ contact.name }}
              </span>
              <span v-if="contact.data" class="text-n-slate-9">
                ({{ Object.values(contact.data).join(', ') }})
              </span>
            </div>
            <p
              v-if="csvContacts.length > 5"
              class="text-xs text-n-slate-11 italic"
            >
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.AND_MORE', {
                  count: csvContacts.length - 5,
                })
              }}
            </p>
          </div>
          <!-- Extra columns detected info -->
          <p v-if="csvColumns.length > 0" class="mt-2 text-xs text-n-brand">
            {{
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.CSV.EXTRA_COLUMNS', {
                count: csvColumns.length,
                columns: csvColumns.join(', '),
              })
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Column Mapping: CSV columns → Template Variables -->
    <div
      v-if="showColumnMapping"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-black2 border border-n-brand/30"
    >
      <div class="flex flex-col gap-1">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.COLUMN_MAPPING.TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.COLUMN_MAPPING.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex flex-col gap-2">
        <div
          v-for="varKey in templateBodyVariables"
          :key="`mapping-${varKey}`"
          class="flex items-center gap-3"
        >
          <span
            class="flex-shrink-0 inline-flex items-center justify-center w-16 px-2 py-1 rounded text-xs font-mono font-medium bg-n-brand/10 text-n-brand"
          >
            {{ formatVariableLabel(varKey) }}
          </span>
          <svg
            class="w-4 h-4 text-n-slate-9 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
          <ComboBox
            v-model="columnMapping[varKey]"
            :options="csvColumnOptions"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.COLUMN_MAPPING.SELECT_COLUMN')
            "
            class="flex-1 [&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          />
        </div>
      </div>
      <p v-if="!isMappingComplete" class="text-xs text-n-ruby-9">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.COLUMN_MAPPING.INCOMPLETE') }}
      </p>
    </div>

    <!-- Funnel/Stage Selector -->
    <div v-if="state.audienceType === 'funnel'" class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label for="funnel" class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.FUNNEL.LABEL') }}
        </label>
        <ComboBox
          id="funnel"
          v-model="state.selectedFunnel"
          :options="funnelOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FUNNEL.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>

      <div v-if="state.selectedFunnel" class="flex flex-col gap-1">
        <label for="stage" class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGE.LABEL') }}
        </label>
        <ComboBox
          id="stage"
          v-model="state.selectedStage"
          :options="stageOptions"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGE.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGE.INFO') }}
        </p>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Throttling info for unofficial API providers -->
    <div
      v-if="isMessageProvider"
      class="p-3 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800"
    >
      <div class="flex items-start gap-2">
        <svg
          class="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div>
          <p class="text-sm font-medium text-amber-800 dark:text-amber-200">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THROTTLING.TITLE') }}
          </p>
          <p class="mt-1 text-xs text-amber-700 dark:text-amber-300">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.THROTTLING.DESCRIPTION') }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
